import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { parseStoredPhone, formatLegacyE164, normalizePhone } from '@/lib/phoneUtils';

const COUNTRIES = [
  { code: '+52', flag: '🇲🇽', name: 'Mexico', short: 'MX' },
  { code: '+1', flag: '🇺🇸', name: 'US / Canada', short: 'US' },
  { code: '+34', flag: '🇪🇸', name: 'Espana', short: 'ES' },
  { code: '+57', flag: '🇨🇴', name: 'Colombia', short: 'CO' },
  { code: '+54', flag: '🇦🇷', name: 'Argentina', short: 'AR' },
  { code: '+56', flag: '🇨🇱', name: 'Chile', short: 'CL' },
  { code: '+51', flag: '🇵🇪', name: 'Peru', short: 'PE' },
  { code: '+593', flag: '🇪🇨', name: 'Ecuador', short: 'EC' },
  { code: '+502', flag: '🇬🇹', name: 'Guatemala', short: 'GT' },
  { code: '+53', flag: '🇨🇺', name: 'Cuba', short: 'CU' },
  { code: '+1', flag: '🇩🇴', name: 'Rep. Dominicana', short: 'DO' },
  { code: '+504', flag: '🇭🇳', name: 'Honduras', short: 'HN' },
  { code: '+503', flag: '🇸🇻', name: 'El Salvador', short: 'SV' },
  { code: '+595', flag: '🇵🇾', name: 'Paraguay', short: 'PY' },
  { code: '+505', flag: '🇳🇮', name: 'Nicaragua', short: 'NI' },
  { code: '+506', flag: '🇨🇷', name: 'Costa Rica', short: 'CR' },
  { code: '+507', flag: '🇵🇦', name: 'Panama', short: 'PA' },
  { code: '+598', flag: '🇺🇾', name: 'Uruguay', short: 'UY' },
  { code: '+591', flag: '🇧🇴', name: 'Bolivia', short: 'BO' },
  { code: '+58', flag: '🇻🇪', name: 'Venezuela', short: 'VE' },
  { code: '+1', flag: '🇵🇷', name: 'Puerto Rico', short: 'PR' },
  { code: '+240', flag: '🇬🇶', name: 'Guinea Ecuatorial', short: 'GQ' },
];

/**
 * PhoneInput (modo dual)
 *
 * Modo LEGACY (4 callers Grupo A):
 *   value:    string persistido (ej. "+525520952500" o valor heredado con espacio)
 *   onChange: fn(e164String)  // recibe E.164 sin espacio: "+525520952500"
 *
 * Modo RICH (Grupo B):
 *   value:        digitos locales sin codigo ("5520952500")
 *   countryCode:  "52" (sin +)
 *   provider:     "twilio" | "gupshup" | "meta_cloud" | "evolution" | "default"
 *   onRichChange: fn({ raw, countryCode, e164, digitsOnly })
 *
 * El modo se activa cuando el caller pasa `onRichChange`.
 */
export default function PhoneInput({
  value,
  onChange,
  onRichChange,
  countryCode,
  provider = 'default',
  placeholder,
  className,
  disabled,
  'data-testid': testId,
}) {
  const isRich = typeof onRichChange === 'function';

  // Estado interno: codigo de pais con "+" y numero local sin codigo.
  const initial = (() => {
    if (isRich) {
      const cc = countryCode ? `+${String(countryCode).replace(/^\+/, '')}` : '+52';
      return { code: cc, number: String(value ?? '').replace(/\D+/g, '') };
    }
    // Legacy: parsear el string persistido
    const parsed = parseStoredPhone(value);
    return { code: `+${parsed.countryCode}`, number: parsed.raw };
  })();

  const [code, setCode] = useState(initial.code);
  const [number, setNumber] = useState(initial.number);

  // Re-sync si el value persistido cambia externamente (load async).
  // IMPORTANTE: si el value que llega del parent es exactamente el que nosotros
  // acabamos de emitir con el estado local, NO re-parsear. De lo contrario, al
  // escribir "5" → emit "+525" → parseStoredPhone("+525") cae al fallback MX y
  // nos regresa `raw="525"` (el countryCode se mete al input).
  useEffect(() => {
    if (isRich) return; // en modo rich el caller controla value directamente
    const currentEmitted = formatLegacyE164(code.replace(/^\+/, ''), number);
    if ((value || '') === currentEmitted) return;
    const parsed = parseStoredPhone(value);
    const nextCode = `+${parsed.countryCode}`;
    if (parsed.raw !== number || nextCode !== code) {
      setCode(nextCode);
      setNumber(parsed.raw);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (nextCode, nextNumber) => {
    const ccDigits = nextCode.replace(/^\+/, '');
    const localDigits = String(nextNumber || '').replace(/\D+/g, '');

    if (isRich) {
      const e164 = formatLegacyE164(ccDigits, localDigits);
      const digitsOnly = localDigits ? `${ccDigits}${localDigits}` : '';
      const formattedForProvider = normalizePhone(localDigits, ccDigits, provider);
      onRichChange({
        raw: localDigits,
        countryCode: ccDigits,
        e164,
        digitsOnly,
        formatted: formattedForProvider,
      });
    } else if (typeof onChange === 'function') {
      onChange(formatLegacyE164(ccDigits, localDigits));
    }
  };

  const handleCodeChange = (newCode) => {
    setCode(newCode);
    emit(newCode, number);
  };

  const handleNumberChange = (e) => {
    // Permitimos que el usuario tipee con separadores pero en el estado limpio
    const cleaned = e.target.value.replace(/\D+/g, '');
    setNumber(cleaned);
    emit(code, cleaned);
  };

  const currentCountry = COUNTRIES.find(c => c.code === code) || COUNTRIES[0];

  return (
    <div className="flex gap-1.5" data-testid={testId}>
      <Select
        value={`${currentCountry.short}-${code}`}
        onValueChange={(v) => handleCodeChange(v.split('-').slice(1).join('-'))}
        disabled={disabled}
      >
        <SelectTrigger
          className="w-[100px] bg-black/20 border-white/10 shrink-0 text-xs px-2"
          data-testid={testId ? `${testId}-code` : undefined}
        >
          <SelectValue>{currentCountry.flag} {code}</SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-[200px]">
          {COUNTRIES.map(c => (
            <SelectItem key={`${c.short}-${c.code}`} value={`${c.short}-${c.code}`} className="text-xs">
              {c.flag} {c.code} {c.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={number}
        onChange={handleNumberChange}
        placeholder={placeholder || '55 1234 5678'}
        className={`bg-black/20 border-white/10 focus:border-primary flex-1 ${className || ''}`}
        data-testid={testId ? `${testId}-number` : undefined}
        disabled={disabled}
        inputMode="tel"
      />
    </div>
  );
}
